import {
  CellClickedEvent,
  ColDef,
  GridReadyEvent,
  IServerSideDatasource,
} from '@ag-grid-community/core';
import AgGridSolid, { AgGridSolidRef } from '@ag-grid-community/solid';
import { Box, Stack } from '@atgfr8/atg-ui-tailwind';
import { alternateRowStyle } from '@components/Grid/utils';
import GridNotification from '@components/GridNotification';
import { fetchCustomers } from '@store/customers/services';
import { Customer } from '@store/customers/types';
import { ConfigManager } from '@utils/ConfigManager';
import { formatAmount } from '@utils/utils';
import { DoNotSolicitCustomers } from '@views/revenueDashboard/overview/components/DoNotSolicitCustomers';
import { get } from 'lodash';
import { DateTime } from 'luxon';
import { createSignal } from 'solid-js';
import { render } from 'solid-js/web';

import { ActiveInActiveCustomer, CustomeInfoDetails } from './components';
import {
  applyCustomerCache,
  fetchCustomerCache,
  saveCustomerCache,
} from './customerGridHelpers';

const statusFilterValues = [
  'Bankruptcy',
  'Collection Hold',
  'Credit Hold',
  'Credit Review',
  'Customer',
  'Deactivated',
  'Dormant',
  'Duplicate',
  'In Collections',
  'In Review',
  'Inactive',
  'Legal Review',
  'Pending',
  'RFP',
  'Archived',
];

const subStatusFilterValues = [
  'Business Structure',
  'Pending Application',
  'Agreement/Forms',
  'Deactivated Carrier Only',
  'Deactivated Claim',
  'Project',
  'Prepay',
];

const cellStyle = {
  padding: '5px',
  'word-break': 'break-word',
  'white-space': 'normal',
  'line-height': '1.6 ',
};

const filterParams = {
  filterOptions: ['startsWith', 'contains', 'endsWith'],
  buttons: ['clear'],
  suppressAndOrCondition: true,
};

const filterTypeMapping = {
  equals: 'EQUALS',
  notEqual: 'NOT_EQUALS',
  greaterThan: 'GREATER_THAN',
  greaterThanOrEqual: 'GREATER_THAN_OR_EQUALS',
  lessThan: 'LESS_THAN',
  lessThanOrEqual: 'LESS_THAN_OR_EQUALS',
  contains: 'CONTAINS',
  notContains: 'NOT_CONTAINS',
  startsWith: 'STARTS_WITH',
  endsWith: 'ENDS_WITH',
  blank: 'BLANK',
  notBlank: 'NOT_BLANK',
  inRange: 'IN_RANGE',
  set: 'EQUALS',
};

const transformFilters = (inputFilters: { [key: string]: unknown }) => {
  return Object.entries(inputFilters).flatMap(([key, filter]) => {
    const {
      filterType,
      type,
      filter: value,
      dateFrom,
      dateTo,
      values,
    } = filter as {
      filterType: string;
      type: keyof typeof filterTypeMapping;
      filter: string;
      dateFrom?: string | null;
      dateTo?: string | null;
      values?: string[];
    };

    if (filterType === 'date') {
      const dateFilters = [dateFrom, dateTo]
        .filter(Boolean)
        .map((date) =>
          DateTime.fromFormat(date as string, 'yyyy-MM-dd HH:mm:ss').toFormat(
            'MM/dd/yyyy',
          ),
        );

      return {
        field: key,
        filterType: filterTypeMapping[type],
        values: dateFilters,
      };
    }

    if (filterType === 'set') {
      // Nothing selected, skip instead of sending values: [null]
      if (!Array.isArray(values) || values.length === 0) {
        return [];
      }

      return {
        field: key,
        filterType: 'IN',
        values,
      };
    }

    return {
      field: key,
      filterType: filterTypeMapping[type],
      values: value ? [value] : [null],
    };
  });
};

const customerColDefs: ColDef[] = [
  {
    field: 'name',
    headerName: 'Customer',
    filter: 'agTextColumnFilter',
    cellStyle: cellStyle,
    minWidth: 280,
    filterParams: filterParams,
    floatingFilter: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    filter: 'agSetColumnFilter',
    cellRenderer: (params: { data: Customer }) => {
      const container = document.createElement('div');
      container.classList.add('flex', 'gap-2', 'items-center');
      const [Component] = createSignal(ActiveInActiveCustomer(params.data));
      render(() => <Component />, container);
      return container;
    },
    cellStyle: cellStyle,
    minWidth: 120,
    floatingFilter: true,
    filterParams: {
      buttons: ['reset'],
      suppressAndOrCondition: true,
      values: statusFilterValues,
    },
  },
  {
    field: 'subStatus',
    headerName: 'Sub-Status',
    filter: 'agSetColumnFilter',
    cellStyle: cellStyle,
    minWidth: 140,
    floatingFilter: true,
    filterParams: {
      buttons: ['reset'],
      suppressAndOrCondition: true,
      values: subStatusFilterValues,
    },
  },
  {
    colId: 'contactName',
    headerName: 'Contact',
    filter: false,
    cellRenderer: (params: { data: Customer }) => {
      const container = document.createElement('div');
      container.classList.add('flex', 'flex-col', 'gap-[2px]', 'h-full');
      const [Component] = createSignal(CustomeInfoDetails(params.data));
      render(() => <Component />, container);
      return container;
    },
    autoHeight: true,
    minWidth: 200,
    cellStyle: cellStyle,
  },
  {
    field: 'creditLimit',
    headerName: 'Credit',
    filter: false,
    valueFormatter: (params) => {
      return formatAmount(params.value as number);
    },
    minWidth: 80,
    maxWidth: 100,
    cellStyle: cellStyle,
  },
  {
    field: 'location',
    headerName: 'Location',
    filter: 'agTextColumnFilter',
    cellStyle: cellStyle,
    minWidth: 120,
    filterParams: filterParams,
    floatingFilter: true,
  },
  {
    field: 'salesManager',
    headerName: 'Sales Manager',
    filter: 'agTextColumnFilter',
    cellStyle: cellStyle,
    minWidth: 120,
    filterParams: filterParams,
    floatingFilter: true,
  },
  {
    field: 'accountManager',
    headerName: 'Account Manager',
    filter: 'agTextColumnFilter',
    cellStyle: cellStyle,
    minWidth: 120,
    filterParams: filterParams,
    floatingFilter: true,
  },
  {
    field: 'createdDate',
    headerName: 'Created Date',
    filter: 'agDateColumnFilter',
    cellStyle: cellStyle,
    minWidth: 140,
    filterParams: {
      suppressAndOrCondition: true,
      filterOptions: ['equals', 'lessThan', 'greaterThan', 'inRange'],
      buttons: ['clear'],
      minValidYear: 1900,
      maxValidYear: new Date().getFullYear() + 1,
    },
    valueFormatter: (params) => {
      const date = DateTime.fromISO(params.value as string);
      if (date.isValid) {
        return `${date.setZone('local').toFormat('MM/dd/yyyy h:mm a')}`;
      }
      return '';
    },
    floatingFilter: true,
  },
  {
    field: 'office',
    headerName: 'Office',
    filter: 'agTextColumnFilter',
    cellStyle: cellStyle,
    minWidth: 100,
    filterParams: filterParams,
    floatingFilter: true,
  },
];

export const CustomerGrid = () => {
  const [gridRef, setGridRef] = createSignal<AgGridSolidRef | null>(null);
  const [rowCount, setRowCount] = createSignal<number>(0);

  const handleCacheSave = () => {
    const api = gridRef()?.api;
    if (api) {
      void saveCustomerCache(api);
    }
  };

  const onGridReady = async (params: GridReadyEvent) => {
    setGridRef(params);

    const cache = await fetchCustomerCache();
    if (cache) {
      applyCustomerCache(params.api, cache);
    }

    const dataSource = {
      getRows: async (params: {
        api: {
          paginationGetCurrentPage: () => number;
        };
        request: {
          filterModel: {
            [key: string]: {
              filter?: string;
              dateFrom?: string | null;
              dateTo?: null;
              filterType: 'date' | 'text' | 'set';
              type: string;
              values: string[];
            };
          };
          sortModel: {
            colId: string;
            sort: string;
          }[];
        };
        success: (data: { rowData: Customer[]; rowCount: number }) => void;
        fail: () => void;
      }) => {
        let orderBy = '';
        let sortBy = '';

        if (params.request.sortModel.length > 0) {
          orderBy = params.request.sortModel[0].colId;
          sortBy = params.request.sortModel[0].sort;
        }

        const filterObject = {
          filters: transformFilters(params.request.filterModel),
          sort: {
            field: orderBy || 'createdDate',
            direction: sortBy || 'desc',
          },
          pageNumber: params.api.paginationGetCurrentPage() + 1,
          pageSize: 50,
        };
        try {
          const data = await fetchCustomers(filterObject);

          if (Boolean(data)) {
            params.success({
              rowData: get(data, 'value.data', []),
              rowCount: get(data, 'value.totalCount', 0),
            });
            setRowCount(get(data, 'value.totalCount', 0));
          }
        } catch (error) {
          gridRef()?.api.showNoRowsOverlay();
          params.fail();
        }
      },
    };
    params.api.setGridOption(
      'serverSideDatasource',
      dataSource as unknown as IServerSideDatasource,
    );
  };

  return (
    <Stack class="h-full gap-1">
      <DoNotSolicitCustomers
        title="Do Not Solicit These Active Customers"
        type="Customer"
      />
      <GridNotification
        rowCount={rowCount() > 0}
        text="Selecting an option below will open the Customer Profile in a new tab."
      />
      <Box class="ag-theme-alpine customer-grid flex flex-col flex-1! min-h-[400px]">
        <AgGridSolid
          ref={gridRef}
          columnDefs={customerColDefs}
          overlayNoRowsTemplate="<span class='ag-overlay-loading-center'>Failed to fetch data from API</span>"
          gridOptions={{
            defaultColDef: {
              flex: 1,
              filter: true,
              autoHeight: true,
            },
            headerHeight: 35,
            rowHeight: 30,
            onGridReady: onGridReady,
            onFilterChanged: () => handleCacheSave(),
            onSortChanged: () => handleCacheSave(),
            onColumnMoved: () => handleCacheSave(),
            onColumnResized: () => handleCacheSave(),
            onColumnVisible: () => handleCacheSave(),
            paginationPageSizeSelector: false,
            rowModelType: 'serverSide',
            pagination: true,
            paginationPageSize: 50,
            cacheBlockSize: 50,
            sideBar: {
              toolPanels: [
                {
                  id: 'columns',
                  labelDefault: 'Columns',
                  labelKey: 'columns',
                  iconKey: 'columns',
                  toolPanel: 'agColumnsToolPanel',
                  toolPanelParams: {
                    suppressRowGroups: true,
                    suppressValues: true,
                    suppressPivots: true,
                    suppressPivotMode: true,
                  },
                },
              ],
              defaultToolPanel: '',
            },
          }}
          getRowStyle={alternateRowStyle}
          onCellClicked={(e: CellClickedEvent) => {
            if (e.colDef.colId === 'contact') return;
            const customerId = (e.data as { id: number }).id;
            if (customerId) {
              const url = ConfigManager.showLayout
                ? `/customer/details/${customerId}`
                : `${ConfigManager.domainUrl}/customer/details/${customerId}`;
              window.open(url, '_blank');
            }
          }}
          pagination
        />
      </Box>
    </Stack>
  );
};
